/**
 * useContacts — W4.8
 *
 * Holt die Contacts-Liste vom Backend (GET /api/v2/contacts).
 * Pagy-basierte Pagination + Search via Query-Param q.
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'

// --- Lokale Typen ---

export type Contact = {
  id: number
  name: string
  email: string | null
  phone: string | null
  address: string | null
  dashboard: boolean | null
  monthly_report: boolean
  broadcast_enabled: boolean
  notes: string | null
  helium_wallet: string | null
  btc_wallet: string | null
  eth_wallet: string | null
  usdt_wallet: string | null
  usdc_wallet: string | null
  payment_method: string | null
  currency: string | null
  iban: string | null
  paypal: string | null
  tag_list: string
  shares_count: number
  shares?: Share[]
  created_at: string
}

export type Share = {
  id: string
  hotspot_id: string
  hotspot_name: string | null
  percentage?: number | null
  fixed_amount?: number | null
  start_date: string | null
  end_date: string | null
}

export type PagyMeta = {
  page: number
  pages: number
  count: number
  items: number
}

export type ContactsResponse = {
  data: Contact[]
  meta: { pagy: PagyMeta }
}

export type ContactsFilters = {
  q?: string
  page?: number
  /** R6/MP: Seitengroesse (Backend-Limit 100) — fuer Select-Listen wie das
   *  Payment-Formular, das moeglichst alle Kontakte auf einmal braucht. */
  perPage?: number
}

// --- Hook ---

type UseContactsResult = {
  contacts: Contact[] | undefined
  meta: PagyMeta | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useContacts(filters: ContactsFilters = {}): UseContactsResult {
  const { q, page, perPage } = filters

  const query = useApi<ContactsResponse>(
    ['contacts', { q, page, perPage }],
    async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.GET('/api/v2/contacts', {
        params: {
          query: {
            ...(q ? { q } : {}),
            ...(page ? { page } : {}),
            ...(perPage ? { per_page: perPage } : {}),
          },
        },
      })

      if (result.response?.status === 404 || result.response?.status >= 500) {
        throw { status: result.response.status }
      }

      return result as { data?: ContactsResponse; error?: unknown }
    },
  )

  return {
    contacts: query.data?.data,
    meta: query.data?.meta?.pagy,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}
